import _ from 'lodash';
import React, {useLayoutEffect, useRef, useState} from 'react';

import {ReportPageViewHeader} from '../pages/ReportPage/ViewReportPage/ReportPageViewHeader';
import {useSelector} from '../state/hooks';
import * as ReportSelectors from '../state/reports/selectors';
import {ReportViewRef} from '../state/reports/types';
import {useViewer} from '../state/viewer/hooks';
import {usePart} from '../state/views/hooks';
import {
  ReportPageNormalized,
  ReportWidthOption,
} from '../state/views/report/types';
import {isOwnView} from '../state/views/util';
import * as S from './FloatingReportDiscussion.styles';

const MIN_REPORT_DEPTH = 150;
interface FloatingReportDiscussionProps {
  viewRef: ReportViewRef;
}

const FloatingReportDiscussion: React.FC<FloatingReportDiscussionProps> = ({
  viewRef,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pastScrollThreshold, setPastScrollThreshold] = useState(false);
  const [outOfBounds, setOutOfBounds] = useState(false);

  const viewer = useViewer();

  useLayoutEffect(() => {
    const scrollHandler = _.throttle(() => {
      setPastScrollThreshold(window.scrollY >= MIN_REPORT_DEPTH);
    }, 50);
    const resizeHandler = _.throttle(() => {
      if (containerRef.current == null) {
        return;
      }
      const {right} = containerRef.current.getBoundingClientRect();
      if (right > S.WIDTH) {
        setOutOfBounds(false);
      } else if (right > S.NARROW_WIDTH) {
        setOutOfBounds(false);
      } else {
        setOutOfBounds(true);
      }
    }, 50);
    scrollHandler();
    resizeHandler();
    window.addEventListener('scroll', scrollHandler);
    window.addEventListener('resize', resizeHandler);
    return () => {
      window.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const view = useSelector(state => state.views.views[viewRef.id]);
  const reportID = view.id;
  if (reportID == null || view.partRef == null) {
    throw new Error('invalid state');
  }
  const viewPart = usePart(view.partRef) as ReportPageNormalized;
  const viewAuthors = viewPart.authors ?? [];
  let authors;
  if (viewAuthors != null && viewAuthors.length > 0) {
    authors = viewAuthors;
  } else {
    authors = [_.pick(view.user, 'username', 'name')];
  }
  const viewerOwnsReport =
    isOwnView(viewer, view) ||
    authors.some(author => author?.username === viewer?.username);

  const reportWidth: ReportWidthOption = useSelector(
    ReportSelectors.getReportWidth(viewRef)
  );

  if (reportWidth !== 'readable') {
    return null;
  }

  return (
    <S.FloatingContainer active={!outOfBounds && pastScrollThreshold}>
      <ReportPageViewHeader
        viewerOwnsReport={viewerOwnsReport}
        isFloatingBar={true}
        shareReportVariant="ghost"
        loginPopupOffset={25}></ReportPageViewHeader>
    </S.FloatingContainer>
  );
};

export default FloatingReportDiscussion;
